import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar.jsx';
import api from '../lib/api.js';

const TIERS = [
  { key: 'starter', label: 'Starter', maps: 5, exports: 4, reports: 4 },
  { key: 'professional', label: 'Professional', maps: 10, exports: 10, reports: 10 },
  { key: 'enterprise', label: 'Enterprise', maps: 35, exports: -1, reports: 35 },
];
const fmtQ = (n) => (n === -1 ? '∞' : n);

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [credModal, setCredModal] = useState(null); // { user, access_key } shown after create

  const [jobFilter, setJobFilter] = useState('');
  const reload = async () => {
    setLoading(true);
    try {
      const [u, j, s] = await Promise.all([
        api.listUsers(),
        api.listJobs({ all: '1', limit: '200' }),
        api.adminStats(),
      ]);
      setUsers(u.users || []);
      setJobs(j.jobs || []);
      setStats(s);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobFilter
    ? jobs.filter(j => {
        const q = jobFilter.toLowerCase();
        return (j.user_email || '').toLowerCase().includes(q)
          || (j.client_name || '').toLowerCase().includes(q)
          || (j.id || '').toLowerCase().includes(q)
          || (j.status || '').toLowerCase().includes(q);
      })
    : jobs;

  useEffect(() => { reload(); }, []);

  const onSaveUser = async (u) => {
    try {
      if (u.id) {
        await api.updateUser(u.id, u);
      } else {
        const res = await api.createUser(u);
        // Show success modal with credentials so admin can share them
        if (res && res.user) {
          setCredModal({ user: res.user, isNew: true });
        }
      }
      setEditing(null); setCreating(false);
      await reload();
    } catch (e) { alert('Save failed: ' + e.message); }
  };

  const onDeleteUser = async (id) => {
    if (!confirm('Delete this user? Their access key will stop working immediately.')) return;
    try { await api.deleteUser(id); await reload(); }
    catch (e) { alert('Delete failed: ' + e.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="tag tag-gold mb-2">Admin</div>
            <h1 className="text-3xl font-bold text-navy">Operator console</h1>
            <p className="text-ink-muted text-sm mt-1">Members, tiers, quotas, and live job activity across the platform.</p>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            <button
              className="inline-flex items-center px-5 py-2.5 rounded-md font-semibold text-sm bg-white border-2 border-navy text-navy hover:bg-navy hover:text-white transition"
              onClick={() => setInviteOpen(true)}
            >
              ✉ Invite user
            </button>
            <a
              href="/admin/hub"
              className="inline-flex items-center px-5 py-2.5 rounded-md font-semibold text-sm bg-white border-2 border-navy text-navy hover:bg-navy hover:text-white transition"
            >
              🎛️ MAPTRIUM Hub
            </a>
            <button
              className="inline-flex items-center px-5 py-2.5 rounded-md font-semibold text-sm bg-gold text-navy border-2 border-gold hover:brightness-95 transition shadow"
              style={{ backgroundColor: '#DBC078', color: '#032953', borderColor: '#DBC078' }}
              onClick={() => { setCreating(true); setEditing(blankUser()); }}
            >
              + Add user
            </button>
          </div>
        </div>

        {/* Helper banner — explains the click-to-edit pattern */}
        <div className="mb-6 bg-gold/10 border border-gold/40 rounded-lg px-4 py-2 text-sm text-navy flex items-center gap-2">
          <span className="font-semibold">Tip:</span>
          <span>Click any member row below to open their account and edit role, tier, quota, or access key.</span>
        </div>
        {inviteOpen && <InviteUserModal onClose={() => setInviteOpen(false)} onCreated={() => { setInviteOpen(false); reload(); }} />}

        {err && <div className="card card-pad mb-6 border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}

        {/* Stat tiles */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <StatTile label="Users" value={stats?.user_count} />
          <StatTile label="Jobs this month" value={stats?.jobs_month} />
          <StatTile label="Completed jobs" value={stats?.jobs_completed} />
          <StatTile label="Active subscriptions" value={stats?.active_paid} suffix={`/ ${stats?.user_count || 0}`} />
        </div>

        {/* Users table */}
        <div className="card mb-8">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between rounded-t-xl bg-white">
            <h2 className="font-semibold text-navy">Members</h2>
            <div className="text-xs text-ink-muted">{users.length} total · scroll right for actions →</div>
          </div>
          {loading && <div className="p-6 text-sm text-ink-muted">Loading…</div>}
          {!loading && (
          <div className="overflow-x-auto rounded-b-xl">
            <table className="w-full text-sm" style={{ minWidth: '1100px' }}>
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-6 py-3">Name / Email</th>
                  <th className="text-left px-6 py-3">Role</th>
                  <th className="text-left px-6 py-3">Tier</th>
                  <th className="text-left px-6 py-3">Auth</th>
                  <th className="text-left px-6 py-3">Quota override</th>
                  <th className="text-left px-6 py-3">Maps used / mo</th>
                  <th className="text-left px-6 py-3">Exports used</th>
                  <th className="text-left px-6 py-3">Reports used</th>
                  <th className="text-left px-6 py-3">Last login</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="px-3 py-3 sticky right-0 bg-slate-50 z-10 shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.08)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr
                    key={u.id}
                    className="hover:bg-gold/10 cursor-pointer transition"
                    onClick={() => setEditing(u)}
                    title="Click anywhere to edit this user"
                  >
                    <td className="px-6 py-3">
                      <div className="font-semibold text-navy underline decoration-dotted decoration-gold underline-offset-4">{u.name || '—'}</div>
                      <div className="text-xs text-ink-muted">{u.email}</div>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`tag ${u.role === 'admin' ? 'tag-gold' : 'tag-navy'}`}>{u.role}</span>
                    </td>
                    <td className="px-6 py-3 capitalize">{u.tier}</td>
                    <td className="px-6 py-3 text-xs">
                      <AuthBadge user={u} />
                    </td>
                    <td className="px-6 py-3 text-xs font-mono">
                      {u.quota_override ? `+${u.quota_override.maps || 0} maps` : <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      <UsageBar used={u.usage?.maps_used || 0} cap={u.usage?.maps_cap || 0} />
                    </td>
                    <td className="px-6 py-3 text-xs">
                      <UsageBar used={u.usage?.exports_used || 0} cap={u.usage?.exports_cap || 0} />
                    </td>
                    <td className="px-6 py-3 text-xs">
                      <UsageBar used={u.usage?.reports_used || 0} cap={u.usage?.reports_cap || 0} />
                    </td>
                    <td className="px-6 py-3 text-xs text-ink-muted whitespace-nowrap">{shortFmt(u.last_login_at) || 'never'}</td>
                    <td className="px-6 py-3">
                      <span className={`tag ${u.status === 'active' ? 'tag-green' : 'tag-red'}`}>{u.status || 'active'}</span>
                    </td>
                    <td
                      className="px-3 py-3 text-right whitespace-nowrap sticky right-0 bg-white shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.08)] group-hover:bg-gold/10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="inline-flex gap-1 justify-end">
                        <button
                          title="Edit user"
                          className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold bg-navy text-white hover:bg-navy/90"
                          onClick={() => setEditing(u)}
                        >
                          Edit
                        </button>
                        <button
                          title="Reset access key"
                          className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold bg-gold text-navy hover:brightness-95"
                          style={{ backgroundColor: '#DBC078', color: '#032953' }}
                          onClick={() => setCredModal({ user: u, isNew: false })}
                        >
                          Key
                        </button>
                        <button
                          title="Delete user"
                          className="inline-flex items-center px-2.5 py-1.5 rounded text-xs font-semibold bg-red-600 text-white hover:bg-red-700"
                          onClick={() => onDeleteUser(u.id)}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Audit timeline */}
        <AuditTimeline />

        {/* Jobs across all users */}
        <div className="card overflow-hidden mt-8">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
            <h2 className="font-semibold text-navy">All jobs across customers</h2>
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <input
                type="text"
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                placeholder="Filter by owner email, client, job id, or status…"
                className="w-full text-sm border border-slate-300 rounded px-3 py-1.5"
              />
              <div className="text-xs text-ink-muted whitespace-nowrap">
                {filteredJobs.length} of {jobs.length}
              </div>
            </div>
          </div>
          {!loading && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-6 py-3">Job</th>
                  <th className="text-left px-6 py-3">Owner</th>
                  <th className="text-left px-6 py-3">Client</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-left px-6 py-3">Outputs</th>
                  <th className="text-left px-6 py-3">Created</th>
                  <th className="text-right px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredJobs.map(j => {
                  const files = ((j.delivery && j.delivery.files) || []);
                  const docx = files.find(f => (f.name || '').toLowerCase().endsWith('.docx'));
                  const pptx = files.find(f => (f.name || '').toLowerCase().endsWith('.pptx'));
                  return (
                  <tr key={j.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => { window.location.href = `/jobs/${j.id}`; }}>
                    <td className="px-6 py-3 font-mono text-xs">
                      <a href={`/jobs/${j.id}`} className="text-navy hover:underline" onClick={(e) => e.stopPropagation()}>{j.id.slice(0, 8)}</a>
                    </td>
                    <td className="px-6 py-3 text-xs">{j.user_email || j.user_id?.slice(0, 8)}</td>
                    <td className="px-6 py-3">{j.client_name || '—'}</td>
                    <td className="px-6 py-3">
                      <span className={`tag ${j.status === 'completed' ? 'tag-green' : j.status === 'failed' ? 'tag-red' : 'tag-amber'}`}>{j.status}</span>
                    </td>
                    <td className="px-6 py-3 text-xs text-ink-muted">{(j.formats || []).join(', ') || '—'}</td>
                    <td className="px-6 py-3 text-xs text-ink-muted">{fmt(j.created_at)}</td>
                    <td className="px-6 py-3 text-xs text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <a href={`/jobs/${j.id}`} className="text-navy hover:underline mr-3">View</a>
                      {docx && (
                        <a href={`/api/jobs/${j.id}/download/${docx.name}`} className="text-gold-dark hover:underline mr-2" title={`Download ${docx.name} (${Math.round((docx.size||0)/1024)} KB)`}>DOCX</a>
                      )}
                      {pptx && (
                        <a href={`/api/jobs/${j.id}/download/${pptx.name}`} className="text-gold-dark hover:underline" title={`Download ${pptx.name} (${Math.round((pptx.size||0)/1024)} KB)`}>PPTX</a>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {filteredJobs.length === 0 && (
                  <tr><td colSpan="7" className="px-6 py-8 text-center text-sm text-ink-muted">
                    {jobFilter ? `No jobs match "${jobFilter}".` : 'No jobs yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {editing && (
        <UserModal
          user={editing}
          creating={creating}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={onSaveUser}
        />
      )}

      {credModal && (
        <CredentialsModal
          user={credModal.user}
          isNew={credModal.isNew}
          onClose={() => setCredModal(null)}
        />
      )}
    </div>
  );
}

function InviteUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: '', name: '', role: 'member', tier: 'starter' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      await api.inviteUser(form);
      onCreated();
    } catch (e) {
      const code = e?.data?.error;
      setErr(code === 'email_taken' ? 'That email already has an account.'
            : code === 'invalid_email' ? 'Please enter a valid email.'
            : e?.data?.detail || e.message || 'Invite failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 space-y-3">
        <h2 className="text-xl font-bold text-navy">Invite a user</h2>
        <p className="text-xs text-ink-muted">MAPTRIUM will email a magic-link the user clicks to set their password and finish signup. Link is valid for 7 days.</p>
        <input className="input" type="email" placeholder="Email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <input className="input" type="text" placeholder="Full name (optional)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <select className="input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <select className="input" value={form.tier} onChange={e => setForm({ ...form, tier: e.target.value })}>
            <option value="solo">Solo</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button className="btn btn-primary" disabled={busy || !form.email}>{busy ? 'Sending…' : 'Send invite'}</button>
        </div>
      </form>
    </div>
  );
}

function AuthBadge({ user }) {
  const method = user.auth_method
    || (user.password_hash ? 'email_password'
        : user.b2c_object_id ? 'microsoft'
        : user.google_subject_id ? 'google'
        : 'access_key');
  const labels = {
    microsoft:      { label: 'Microsoft', cls: 'bg-sky-100 text-sky-900' },
    google:         { label: 'Google',    cls: 'bg-rose-100 text-rose-900' },
    email_password: { label: 'Email',     cls: 'bg-emerald-100 text-emerald-900' },
    access_key:     { label: 'Access-key',cls: 'bg-slate-100 text-slate-700' },
  };
  const m = labels[method] || labels.access_key;
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded ${m.cls}`}>{m.label}</span>
      {user.invite_pending && <span className="text-[10px] text-amber-700 italic">invite pending</span>}
    </div>
  );
}

function CredentialsModal({ user, isNew, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = (txt) => {
    navigator.clipboard.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const onboardingText =
`MAPTRIUM access for ${user.name || user.email}

Sign in at: https://maptrium.com/login

User ID (email):  ${user.email}
Access key:       ${user.access_key}

Tier: ${user.tier}
Status: ${user.status}

Please keep your access key private. Email info@atlasgeo.ai if you need it reset.`;

  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-6 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-navy">{isNew ? 'User created' : 'User credentials'}</h2>
          <button className="text-ink-muted hover:text-navy" onClick={onClose}>✕</button>
        </div>
        <div className="p-6 space-y-4">
          {isNew && (
            <div className="card-pad bg-gold/20 rounded-lg text-sm text-navy">
              <span className="font-semibold">Account ready.</span> Share the credentials below with the user. The access key is only fully shown here once.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <KV label="Name" value={user.name || '—'} />
            <KV label="Email (user ID)" value={user.email} copy onCopy={copy} />
            <KV label="Role" value={user.role} />
            <KV label="Tier" value={user.tier} />
            <KV label="Status" value={user.status || 'active'} />
            <KV label="Created" value={fmt(user.created_at)} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted mb-1">Access key</div>
            <div className="flex gap-2">
              <code className="flex-1 px-3 py-2 bg-slate-100 rounded-md text-sm font-mono break-all">{user.access_key}</code>
              <button className="btn btn-sm btn-primary" onClick={() => copy(user.access_key)}>Copy</button>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-muted mb-1">Onboarding message (copy/paste to email)</div>
            <textarea className="input font-mono text-xs" rows={9} readOnly value={onboardingText} />
            <div className="mt-2 flex justify-end">
              <button className="btn btn-sm btn-gold" onClick={() => copy(onboardingText)}>{copied ? '✓ Copied' : 'Copy onboarding message'}</button>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end bg-slate-50">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, copy, onCopy }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-navy font-mono text-sm break-all">{value || '—'}</span>
        {copy && value && (
          <button className="text-xs text-ink-muted hover:text-navy" onClick={() => onCopy(value)} title="Copy">⧉</button>
        )}
      </div>
    </div>
  );
}

function shortFmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString(); } catch { return ''; }
}

function blankUser() {
  return { name: '', email: '', role: 'member', tier: 'starter', status: 'active', access_key: '', quota_override: { maps: 0, exports: 0, reports: 0 } };
}

function AuditTimeline() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const d = await api.adminAudit({ limit: 200 });
        setRows(d.rows || []);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const visible = rows.filter(r => {
    if (!filter) return true;
    const hay = `${r.kind || ''} ${r.user_id || ''} ${r.target || ''} ${r.filename || ''} ${r.tier || ''}`.toLowerCase();
    return hay.includes(filter.toLowerCase());
  });

  return (
    <div className="card overflow-hidden mt-8">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-navy">Activity (last 200 events)</h2>
        <input className="input max-w-xs text-sm" placeholder="Filter by kind, user, or target…" value={filter} onChange={e => setFilter(e.target.value)} />
      </div>
      {loading && <div className="p-6 text-sm text-ink-muted">Loading…</div>}
      {err && <div className="p-6 text-sm text-red-700">{err}</div>}
      {!loading && !err && visible.length === 0 && <div className="p-6 text-sm text-ink-muted">No events match.</div>}
      {!loading && !err && visible.length > 0 && (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="text-left px-6 py-2">When</th>
              <th className="text-left px-6 py-2">Kind</th>
              <th className="text-left px-6 py-2">Actor</th>
              <th className="text-left px-6 py-2">Target / detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map((r, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-6 py-1.5 text-xs text-ink-muted whitespace-nowrap font-mono">{fmtTs(r.ts)}</td>
                <td className="px-6 py-1.5"><span className={`tag ${kindTag(r.kind)}`}>{r.kind || '—'}</span></td>
                <td className="px-6 py-1.5 text-xs font-mono">{(r.user_id || '').slice(0, 12)}</td>
                <td className="px-6 py-1.5 text-xs text-ink-muted truncate max-w-md">{auditDetail(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fmtTs(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}
function kindTag(k) {
  if (!k) return 'tag-navy';
  if (k.startsWith('user_create')) return 'tag-green';
  if (k.startsWith('user_delete')) return 'tag-red';
  if (k.startsWith('billing_subscription_active')) return 'tag-green';
  if (k.startsWith('billing_subscription_canceled')) return 'tag-red';
  if (k.startsWith('agol_publish_failed') || k.endsWith('_failed')) return 'tag-red';
  if (k.startsWith('agol')) return 'tag-gold';
  if (k.startsWith('billing')) return 'tag-gold';
  if (k.startsWith('file_download')) return 'tag-navy';
  if (k.startsWith('job_run')) return 'tag-amber';
  return 'tag-navy';
}
function auditDetail(r) {
  const parts = [];
  if (r.target) parts.push(`target=${r.target}`);
  if (r.filename) parts.push(`file=${r.filename}`);
  if (r.tier) parts.push(`tier=${r.tier}`);
  if (r.formats) parts.push(`formats=${Array.isArray(r.formats) ? r.formats.join(',') : r.formats}`);
  if (r.section_count !== undefined) parts.push(`sections=${r.section_count}`);
  if (r.verdict) parts.push(`verdict=${r.verdict}`);
  if (r.count !== undefined) parts.push(`count=${r.count}`);
  if (r.item_id) parts.push(`agol_item=${r.item_id}`);
  if (r.error) parts.push(`err=${String(r.error).slice(0, 80)}`);
  return parts.join(' · ');
}

function StatTile({ label, value, suffix }) {
  return (
    <div className="card card-pad">
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-navy">{value ?? '—'}</span>
        {suffix && <span className="text-ink-muted text-sm">{suffix}</span>}
      </div>
    </div>
  );
}

function UsageBar({ used, cap }) {
  const unlimited = cap === -1 || cap === undefined || cap === null;
  const pct = !unlimited && cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const colorClass = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        {!unlimited && <div className={`h-full ${colorClass}`} style={{ width: pct + '%' }} />}
      </div>
      <span className="font-mono text-xs whitespace-nowrap">{used} / {unlimited ? '∞' : cap}</span>
    </div>
  );
}

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleDateString(); } catch { return ts; }
}

function UserModal({ user, creating, onClose, onSave }) {
  const [u, setU] = useState({ ...user, quota_override: user.quota_override || { maps: 0, exports: 0, reports: 0 } });
  const set = (k, v) => setU(prev => ({ ...prev, [k]: v }));
  const setQ = (k, v) => setU(prev => ({ ...prev, quota_override: { ...prev.quota_override, [k]: Number(v || 0) } }));

  const tier = TIERS.find(t => t.key === u.tier) || TIERS[0];

  return (
    <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-6 z-40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-navy">{creating ? 'Add user' : `Edit ${u.name || u.email}`}</h2>
          <button className="text-ink-muted hover:text-navy" onClick={onClose}>✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="label">Name</label><input className="input" value={u.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div><label className="label">Email</label><input className="input" value={u.email || ''} onChange={e => set('email', e.target.value)} type="email" /></div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">Role</label>
              <select className="input" value={u.role} onChange={e => set('role', e.target.value)}>
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input" value={u.status || 'active'} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Tier</label>
            <select className="input" value={u.tier} onChange={e => set('tier', e.target.value)}>
              {TIERS.map(t => <option key={t.key} value={t.key}>{t.label}: {fmtQ(t.maps)} maps, {fmtQ(t.exports)} exports, {fmtQ(t.reports)} reports / mo</option>)}
            </select>
          </div>
          <div>
            <label className="label">Quota override (additive on top of tier)</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] text-ink-muted mb-1">EXTRA MAPS</div>
                <input className="input" type="number" min="0" value={u.quota_override?.maps || 0} onChange={e => setQ('maps', e.target.value)} />
              </div>
              <div>
                <div className="text-[10px] text-ink-muted mb-1">EXTRA EXPORTS</div>
                <input className="input" type="number" min="0" value={u.quota_override?.exports || 0} onChange={e => setQ('exports', e.target.value)} />
              </div>
              <div>
                <div className="text-[10px] text-ink-muted mb-1">EXTRA REPORTS</div>
                <input className="input" type="number" min="0" value={u.quota_override?.reports || 0} onChange={e => setQ('reports', e.target.value)} />
              </div>
            </div>
            <div className="mt-2 text-xs text-ink-muted">
              Effective monthly: <span className="font-mono text-navy">{fmtQ(tier.maps === -1 ? -1 : tier.maps + (u.quota_override?.maps || 0))}</span> maps · <span className="font-mono text-navy">{fmtQ(tier.exports === -1 ? -1 : tier.exports + (u.quota_override?.exports || 0))}</span> exports · <span className="font-mono text-navy">{fmtQ(tier.reports === -1 ? -1 : tier.reports + (u.quota_override?.reports || 0))}</span> reports
            </div>
          </div>
          {creating && (
            <div>
              <label className="label">Access key</label>
              <input className="input" value={u.access_key || ''} onChange={e => set('access_key', e.target.value)} placeholder="Leave blank to auto-generate" />
              <div className="mt-1 text-xs text-ink-muted">Shared with the user securely. They sign in with this on the /login page.</div>
            </div>
          )}
          {!creating && u.id && (
            <div>
              <label className="label">Reset access key</label>
              <div className="flex gap-2">
                <input
                  className="input flex-1 font-mono"
                  value={u.access_key || ''}
                  onChange={e => set('access_key', e.target.value)}
                  placeholder="Type a new key or click Generate"
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => set('access_key', 'as_' + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 14))}
                >Generate</button>
              </div>
              <div className="mt-1 text-xs text-amber-700">
                Changing this immediately revokes their old key. Send the new value to the user securely; the system never emails it.
              </div>
            </div>
          )}
          {!creating && u.id && <AgolAdminPanel userId={u.id} />}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(u)}>{creating ? 'Create user' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

function AgolAdminPanel({ userId }) {
  const [agol, setAgol] = useState(null);
  const [form, setForm] = useState({ org_url: '', username: '', password: '', publish_default: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  // Lazy-load when opened. The backend doesn't yet have a per-user GET so we
  // pull the user record via listUsers (already cached on parent), or just
  // start blank and let the admin (re)write.
  const onSave = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await api.saveAgolForUser(userId, payload);
      setMsg('AGOL settings saved for this user.');
      setForm(f => ({ ...f, password: '' }));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-t border-slate-200 pt-4">
      <button type="button" onClick={() => setOpen(o => !o)} className="text-sm font-semibold text-navy flex items-center gap-1 hover:underline">
        {open ? '▾' : '▸'} ArcGIS Online (AGOL) for this user
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-ink-muted">
            Save AGOL credentials on behalf of this user. Password is encrypted at rest. They (or you) can then publish job GeoJSONs as hosted Feature Layers in their AGOL org.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="label">Portal URL</label><input className="input" placeholder="https://yourorg.maps.arcgis.com" value={form.org_url} onChange={e => setForm({ ...form, org_url: e.target.value })} /></div>
            <div><label className="label">Username</label><input className="input" placeholder="agol_username" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} autoComplete="off" /></div>
          </div>
          <div>
            <label className="label">Password</label>
            <input type="password" className="input" placeholder="Leave blank to keep saved password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
          </div>
          <label className="flex gap-2 items-center text-xs">
            <input type="checkbox" checked={form.publish_default} onChange={e => setForm({ ...form, publish_default: e.target.checked })} />
            <span>Auto-publish every completed job to their AGOL</span>
          </label>
          {msg && <div className="text-xs text-emerald-700">{msg}</div>}
          {err && <div className="text-xs text-red-700">{err}</div>}
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || (!form.org_url || !form.username)} onClick={onSave}>{busy ? 'Saving…' : 'Save AGOL for this user'}</button>
        </div>
      )}
    </div>
  );
}
